import React, { useState } from 'react';
import {
  DateParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import moment from 'moment';
import {
  Grid,
  MenuItem,
  Stack,
  TextField,
  Box, Button,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { DatePicker } from '@mui/x-date-pickers';

import { AppBar, DebouncedTextField, Empty, Error, Loader } from '@/components';
import { OrderDirections, ORDER_DIRECTIONS } from '@/utils/order-directions';
import { TECH_REQUEST_STATUSES } from '@/utils/tech-request-statuses';
import { TechnicalRequestsTable, CreateTechnicalRequest } from './components';
import {useLazyGetTechnicalRequestsQuery} from "@/services/technical-request";

const TechnicalRequestsPage: React.FC = () => {
  const [{ search, order_direction, status, page, date_from, date_to }, setQuery] =
      useQueryParams({
        search: withDefault(StringParam, ''),
        page: withDefault(NumberParam, 1),
        order_direction: withDefault(StringParam, OrderDirections.DESC),
        status: withDefault(StringParam, ''),
        date_from: withDefault(DateParam, moment().toDate()),
        date_to: withDefault(DateParam, moment().toDate()),
      });

  const [createModalOpened, setOpenedCreateModal] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [
    trigger,
    {
      data: technicalRequests,
      isLoading,
      isFetching,
      isSuccess,
      isError,
    },
  ] = useLazyGetTechnicalRequestsQuery();

  const buildRequest = (overridePage = page) => ({
    params: {
      search: search || null,
      order_direction,
      status,
      page: overridePage,
      order_by: 'id',
      date_from: date_from ? moment(date_from).format('YYYY-MM-DD') : null,
      date_to:   date_to   ? moment(date_to).format('YYYY-MM-DD') : null,
    },
  });

  const onSearch = () => {
    setQuery({ page: 1 });
    setHasSearched(true);
    trigger(buildRequest(1));
  };

  const onChangePage = (newPage: number) => {
    setQuery({ page: newPage });
    trigger(buildRequest(newPage));
  };

  return (
      <>
        <AppBar title="Технические заявки" />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Stack direction="row" spacing={3} alignItems="flex-start">
              <Grid container spacing={2}>
                {/* Поиск текста */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DebouncedTextField
                      type="search"
                      name="search"
                      label="Поиск"
                      placeholder="Введите значение"
                      value={search}
                      onChange={e => setQuery({ search: e.target.value, page: 1 })}
                      variant="filled"
                      fullWidth
                      size="small"
                  />
                </Grid>

                {/* Сортировка */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      value={order_direction}
                      label="Сортировать по"
                      disabled={isLoading}
                      onChange={e => setQuery({ order_direction: e.target.value })}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    {ORDER_DIRECTIONS.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Статус */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <TextField
                      select
                      value={status}
                      label="Статус"
                      disabled={isLoading}
                      onChange={e => setQuery({ status: e.target.value })}
                      variant="filled"
                      fullWidth
                      size="small"
                  >
                    <MenuItem value="">Все</MenuItem>
                    {TECH_REQUEST_STATUSES.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                {/* Дата от */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата от"
                      value={date_from}
                      minDate={moment(date_to).subtract(6, 'months')}
                      maxDate={moment(date_to)}
                      onChange={v => setQuery({ date_from: v ? moment(v).toDate() : undefined })}
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField {...params} variant="filled" size="small" fullWidth />
                      )}
                  />
                </Grid>

                {/* Дата до */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <DatePicker
                      mask="__.__.____"
                      label="Дата до"
                      value={date_to}
                      maxDate={moment()}
                      onChange={v => setQuery({ date_to: v ? moment(v).toDate() : undefined })}
                      disabled={isLoading || isFetching}
                      renderInput={params => (
                          <TextField {...params} variant="filled" size="small" fullWidth />
                      )}
                  />
                </Grid>

                {/* Кнопка «Поиск» */}
                <Grid item lg={3} md={3} sm={6} xs={12}>
                  <LoadingButton
                      loading={isFetching}
                      disabled={isFetching}
                      onClick={onSearch}
                      size="large"
                      variant="contained"
                      fullWidth
                  >
                    Поиск
                  </LoadingButton>
                </Grid>
              </Grid>

              <Box sx={{ width: 210 }}>
                <Button
                    disabled={isLoading}
                    onClick={() => setOpenedCreateModal(true)}
                    size="large"
                    variant="contained"
                    fullWidth
                >
                  Добавить
                </Button>
              </Box>
            </Stack>
          </Grid>

          <Grid item xs={12}>
            {!hasSearched ? (
                <Empty text="Нажмите «Поиск», чтобы увидеть данные" />
            ) : isLoading ? (
                <Loader />
            ) : isError ? (
                <Error text="Произошла ошибка с загрузкой технических заявок! Пожалуйста, попробуйте позже" />
            ) : isSuccess && technicalRequests?.data?.length ? (
                <TechnicalRequestsTable
                    list={technicalRequests}
                    page={page}
                    onChangePage={onChangePage}
                />
            ) : (
                <Empty text="Технические заявки отсутствуют" />
            )}
          </Grid>
        </Grid>

        <CreateTechnicalRequest
            open={createModalOpened}
            onClose={() => setOpenedCreateModal(false)}
        />
      </>
  );
};

export default TechnicalRequestsPage;
